/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback } from 'react'
import ClaimSharedTable from './ClaimSharedTable'
import ClaimExpandedContent from './ClaimExpandedContent'
import StatusBadge, {
  StatusTypes,
} from '@components/common/StatusBadge/StatusBadge.component'
import '../css/ClaimsManagement.css'
import { Claim, ClaimStatus } from '@dataTypes/claimFeature.types'
import { formatDate } from '@utils/date.utils'
import ActionButtons from '@components/common/ActionButtons/ActionButtons.component'
import ViewClaimPostingInfo from '@components/layout/Modals/ViewClaimInfo/ViewClaimInfo.component'
import { EmptyState } from '@components/ui'

type ClaimFeatureProps = {
  onEditClaim: (claim: Claim) => void
  claims: Claim[]
  filters: Record<string, string[]>
  activeTab: 'pre-auth' | 'tracking' | 'claims'
  searchQuery: string
  onDeleteClaim: (id: string) => void
  /**
   * Claim id to auto-open the ViewClaimPostingInfo modal for on mount, and
   * again whenever this value changes. Used by the Claims notification
   * deep-link (`/claims` with `location.state.{claimId, viewClaim: true}`).
   * The lookup waits until the matching claim appears in `claims` so the
   * modal isn't opened with a stale or missing object.
   */
  initialViewClaimId?: string | null
  /**
   * Fired after the deep-link target has been consumed (modal opened OR the
   * claim wasn't found). Parent uses this to clear `location.state` so a
   * remount/back/forward can't replay the open.
   */
  onInitialViewConsumed?: () => void
  emptyActionLabel?: string
  onEmptyAction?: () => void
}

type SortConfig = {
  key: string | null
  direction: 'asc' | 'desc'
}

const ClaimFeature: React.FC<ClaimFeatureProps> = ({
  onEditClaim,
  claims,
  filters,
  activeTab,
  searchQuery,
  onDeleteClaim,
  initialViewClaimId,
  onInitialViewConsumed,
  emptyActionLabel,
  onEmptyAction,
}) => {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: 'asc',
  })
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [selectedClaim, setSelectedClaim] = useState<Claim | null>(null)

  // Auto-open the ViewClaimPostingInfo modal for a deep-linked claim id.
  // Only fires once per `initialViewClaimId` value — `appliedRef` mirrors the
  // pattern used in FormExpandableTable / CompletedFormsTable so a manual
  // close afterwards isn't re-clobbered on the next render. Lookup uses raw
  // `claims` (the prop here is already filtered for the active tab/patient by
  // the parent), so the deep-link target reliably appears once data loads.
  //
  // When `initialViewClaimId` transitions back to `null` (parent acknowledged
  // consumption), we reset `appliedRef` so a subsequent click of the same
  // notification opens the modal again instead of being deduped.
  const appliedInitialViewClaimIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (initialViewClaimId == null) {
      appliedInitialViewClaimIdRef.current = null
      return
    }
    const target = initialViewClaimId
    if (appliedInitialViewClaimIdRef.current === target) return
    if (claims.length === 0) return
    appliedInitialViewClaimIdRef.current = target
    const match = claims.find((c) => c.id === target)
    if (match) {
      setSelectedClaim(match)
      setIsViewModalVisible(true)
    }
    // Notify regardless of match so the parent can drop `location.state`;
    // a stale notification (claim since deleted) shouldn't keep replaying.
    onInitialViewConsumed?.()
  }, [initialViewClaimId, claims, onInitialViewConsumed])

  const toggleRowExpansion = useCallback((id: string) => {
    setExpandedRows((prev) => (prev.has(id) ? new Set() : new Set([id])))
  }, [])

  const handleDeleteClaim = useCallback(
    (id: string) => {
      onDeleteClaim(id)
    },
    [onDeleteClaim],
  )

  const handleSort = (key: string): void => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
      return { key, direction }
    })
  }

  const filteredClaims = claims.filter((claim) => {
    const { status = [], insuranceCarrier = [] } = filters

    const matchesStatus =
      status.length === 0 || status.includes(claim.generalStatus!)
    const matchesInsuranceCarrier =
      insuranceCarrier.length === 0 ||
      insuranceCarrier.includes(claim.insuranceCarrier)

    return matchesStatus && matchesInsuranceCarrier
  })

  const sortedClaims = [...filteredClaims].sort((a, b) => {
    if (!sortConfig.key) return 0
    const aValue = a[sortConfig.key as keyof Claim]
    const bValue = b[sortConfig.key as keyof Claim]

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue
    }
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue)
    }
    return 0
  })

  const handleViewClaimPostingInfo = useCallback((claim: Claim) => {
    setSelectedClaim(claim)
    setIsViewModalVisible(true)
  }, [])

  const handleCloseModal = (): void => {
    setIsViewModalVisible(false)
    setSelectedClaim(null)
  }

  const columns = [
    {
      key: 'id',
      label: activeTab === 'claims' ? 'Claim ID' : 'Pre-Auth ID',
      sortable: true,
      style: { width: '160px' },
      formatter: (_: any, row: Claim) => (
        <div
          className="toggle-claim-container d-flex"
          onClick={() => toggleRowExpansion(row.id)}
        >
          <span className="claim-id-text">{row.providerClaimId}</span>
          <button
            className={`expand-button ${expandedRows.has(row.id) ? 'expanded' : ''}`}
            onClick={(e) => {
              e.stopPropagation()
              toggleRowExpansion(row.id)
            }}
          >
            {expandedRows.has(row.id) ? '▼' : '▶'}
          </button>
        </div>
      ),
    },
    {
      key: 'createdAt',
      label: 'Date submitted',
      sortable: true,
      formatter: (_: any, row: Claim) => formatDate(row.createdAt),
      style: { width: '160px' },
    },
    {
      key: 'patientName',
      label: 'Patient name',
      sortable: true,
      style: { width: '180px' },
    },
    {
      key: 'insuranceCarrier',
      label: 'Payer',
      sortable: true,
      style: { width: 'auto' },
    },
    {
      key: 'claimAmount',
      label: 'Claim amt.',
      sortable: true,
      style: { width: '160px' },
    },
    {
      key: 'amountPaid',
      label: 'Approved ins.',
      sortable: true,
      style: { width: '160px' },
      formatter: (_: any, row: Claim) =>
        row.generalStatus === ClaimStatus.UnderReview ||
        row.generalStatus === ClaimStatus.Rejected ||
        row.generalStatus === ClaimStatus.Draft
          ? '-'
          : row.amountPaid || '-',
    },
    {
      key: 'status',
      label: 'Status',
      style: { width: '200px', 'text-align': 'left' },
      formatter: (_: any, row: Claim) => (
        <StatusBadge
          status={row.generalStatus as unknown as StatusTypes}
          type="claims"
        />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      style: { width: '160px' },
      headerTextAlign: 'center',
      formatter: (_: any, row: Claim) => {
        return (
          <ActionButtons
            {...(row.generalStatus === 'Rejected' ||
            row.generalStatus === 'Draft'
              ? {
                  onEdit: () => onEditClaim(row),
                  onDelete: () => handleDeleteClaim(row.id),
                }
              : {})}
            {...(row.generalStatus !== 'Draft'
              ? { onView: () => handleViewClaimPostingInfo(row) }
              : {})}
            showView={true}
            showMoreOptions={false}
          />
        )
      },
    },
  ]

  return (
    <div
      key={`${sortConfig.key}-${sortConfig.direction}-${filteredClaims.map((c) => c.id).join('-')}`}
      className={
        sortedClaims.length === 0 ? 'claim-feature-empty-host' : undefined
      }
    >
      {sortedClaims.length === 0 ? (
        <EmptyState
          className="claims-empty"
          iconClass="mgc mgc_receive_money_fill"
          title={
            activeTab === 'pre-auth' ? 'No pre-auths found' : 'No claims found'
          }
          description={
            activeTab === 'pre-auth'
              ? 'No pre-auths match your search.'
              : 'No claims match your search.'
          }
          actionLabel={emptyActionLabel}
          onAction={onEmptyAction}
        />
      ) : (
        <ClaimSharedTable
          columns={columns}
          data={sortedClaims}
          pageSize={10}
          activeTab={activeTab}
          enableTableScroll={false}
          additionalRows={(claim) =>
            expandedRows.has(claim.id) && (
              <tr className="expanded-row-content">
                <td colSpan={columns.length} className="p-0">
                  <ClaimExpandedContent claim={claim} />
                </td>
              </tr>
            )
          }
          rowClassName={(row: Claim) =>
            expandedRows.has(row.id) ? 'expanded-row-open' : ''
          }
          onRowClick={(row: Claim) => toggleRowExpansion(row.id)}
          onHeaderClick={handleSort}
          sortConfig={sortConfig}
        />
      )}
      {isViewModalVisible && selectedClaim && (
        <ViewClaimPostingInfo
          isVisible={isViewModalVisible}
          closeModal={handleCloseModal}
          claim={selectedClaim}
          claimType={activeTab === 'pre-auth' ? 'Pre-Authorization' : 'Claims'}
        />
      )}
    </div>
  )
}

export default React.memo(ClaimFeature)
